import { BaseService } from '@/chassis/data/services/base-service';
import { BaseFormService, ValidationRules } from '@/chassis/data/services/base-form-service';
import { createSimpleNormalizer } from '@/chassis/data/services/normalizer-utils';

export interface RepresentationRecord {
    id: string;
    representativeUserId: string;
    requestId?: string;
    representationType: string;
    categoryId: string;
    status: string;
    active?: boolean;
    validFrom: string;
    validUntil?: string;
    grantedPowers?: string[];
    limitations?: string;
    financialLimit?: number;
    legalDocumentId?: string;
    legalDocumentNumber?: string;
    registrationDate?: string;
    registrationOffice?: string;
    justification?: string;
    approvalDate?: string;
    approvedBy?: string;
    revokedDate?: string;
    revocationReason?: string;
    revokedBy?: string;
    usageCount?: number;
    lastUsedDate?: string;
    notes?: string;
    allowsSubdelegation?: boolean;
    requiresConfirmation?: boolean;
    isMonitored?: boolean;
    createdAt?: string;
    updatedAt?: string;
    entitySupplierId?: string | null;
    entityPublicAgencyId?: string | null;
    createdBy?: string;
    createdByName?: string;
    createdByUser?: string;
    updatedBy?: string;
    updatedByName?: string;
    updatedByUser?: string;
}

export interface RepresentationFilters {
    searchTerm?: string;
    representationType?: string;
    status?: string;
    active?: boolean;
    representativeUserId?: string;
    page?: number;
    limit?: number;
}

export interface CreateRepresentationDto {
    representativeUserId: string;
    requestId?: string;
    representationType: string;
    categoryId: string;
    status: string;
    validFrom: string;
    validUntil?: string;
    grantedPowers?: string[];
    limitations?: string;
    financialLimit?: number;
    legalDocumentId?: string;
    legalDocumentNumber?: string;
    registrationDate?: string;
    registrationOffice?: string;
    justification?: string;
    approvalDate?: string;
    approvedBy?: string;
    revokedDate?: string;
    revocationReason?: string;
    revokedBy?: string;
    usageCount?: number;
    lastUsedDate?: string;
    notes?: string;
    allowsSubdelegation?: boolean;
    requiresConfirmation?: boolean;
    isMonitored?: boolean;
    active?: boolean;
    entitySupplierId?: string | null;
    entityPublicAgencyId?: string | null;
}

export interface UpdateRepresentationDto extends Partial<CreateRepresentationDto> {}

export type RepresentationFormData = CreateRepresentationDto & {
    createdAt?: string;
    updatedAt?: string;
    createdBy?: string;
    createdByName?: string;
    createdByUser?: string;
    updatedBy?: string;
    updatedByName?: string;
    updatedByUser?: string;
    active?: boolean;
};

const baseNormalizeRepresentationRecord = createSimpleNormalizer<RepresentationRecord>();

export const resolveRepresentationActive = (record: Partial<RepresentationRecord>): boolean => {
    const status = (record.status ?? '').toString().toUpperCase();
    if (!status) return true;
    if (status === 'ACTIVE' || status === 'ATIVA' || status === 'ATIVO') return true;
    if (status === 'SUSPENDED' || status === 'REVOKED' || status === 'EXPIRED') return false;
    if (status === 'PENDING_VALIDATION') return true;
    return false;
};

export const normalizeRepresentationRecord = (record: RepresentationRecord) => {
    const normalized = baseNormalizeRepresentationRecord(record);
    const active = resolveRepresentationActive(normalized);
    normalized.active = active;
    return normalized;
};

class RepresentationsService extends BaseService<RepresentationRecord, CreateRepresentationDto, UpdateRepresentationDto, RepresentationFilters> {
    constructor() {
        super({
            basePath: '/representations',
            normalizeRecord: normalizeRepresentationRecord,
        });
    }
}

export const representationsService = new RepresentationsService();

const representationFormDefaults: RepresentationFormData = {
    representativeUserId: '',
    requestId: '',
    representationType: '',
    categoryId: '',
    status: 'ACTIVE',
    active: true,
    validFrom: new Date().toISOString().split('T')[0],
    grantedPowers: [],
    limitations: '',
    legalDocumentId: '',
    legalDocumentNumber: '',
    justification: '',
    notes: '',
    allowsSubdelegation: false,
    requiresConfirmation: false,
    isMonitored: false,
    entitySupplierId: null,
    entityPublicAgencyId: null,
    createdAt: undefined,
    updatedAt: undefined,
    createdBy: undefined,
    createdByName: undefined,
    createdByUser: undefined,
    updatedBy: undefined,
    updatedByName: undefined,
    updatedByUser: undefined,
};

const entitySelectionRequired = ValidationRules.custom((_value, data: CreateRepresentationDto) => {
    const hasSupplier = typeof data.entitySupplierId === 'string' && data.entitySupplierId.trim().length > 0;
    const hasAgency = typeof data.entityPublicAgencyId === 'string' && data.entityPublicAgencyId.trim().length > 0;
    if (!hasSupplier && !hasAgency) {
        return 'Informe o identificador da entidade vinculada (fornecedor ou órgão público)';
    }
    return true;
});

const entitySelectionExclusive = ValidationRules.custom((_value, data: CreateRepresentationDto) => {
    const hasSupplier = typeof data.entitySupplierId === 'string' && data.entitySupplierId.trim().length > 0;
    const hasAgency = typeof data.entityPublicAgencyId === 'string' && data.entityPublicAgencyId.trim().length > 0;
    if (hasSupplier && hasAgency) {
        return 'Informe apenas um identificador de entidade (fornecedor ou órgão público)';
    }
    return true;
});

const ensureStringArray = (value: unknown): string[] => {
    if (Array.isArray(value)) {
        return value.map((entry) => String(entry).trim()).filter(Boolean);
    }

    if (typeof value === 'string') {
        return value
            .split(',')
            .map((token) => token.trim())
            .filter(Boolean);
    }

    return [];
};

const toTrimmedString = (value?: string | null) => {
    if (typeof value !== 'string') {
        return undefined;
    }

    const trimmed = value.trim();
    return trimmed.length ? trimmed : undefined;
};

const toOptionalNumber = (value: unknown): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return undefined;
        const numeric = Number(trimmed);
        return Number.isFinite(numeric) ? numeric : undefined;
    }

    return undefined;
};

const normalizeBoolean = (value: unknown, fallback: boolean) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
        if (value.toLowerCase() === 'true') return true;
        if (value.toLowerCase() === 'false') return false;
    }
    return fallback;
};

const normalizeEntityId = (value: unknown) => {
    if (value === null) return null;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed.length ? trimmed : null;
    }
    return undefined;
};

const sanitizeRepresentationPayload = (formData: Partial<RepresentationFormData>): Partial<CreateRepresentationDto> => {
    const { createdAt, updatedAt, createdBy, createdByName, createdByUser, updatedBy, updatedByName, updatedByUser, active, ...submittable } = formData;

    const payload: Partial<CreateRepresentationDto> = {
        representativeUserId: (submittable.representativeUserId ?? '').toString().trim(),
    };

    if ('requestId' in submittable) {
        payload.requestId = toTrimmedString(submittable.requestId) ?? '';
    }

    if ('representationType' in submittable) {
        const normalized = toTrimmedString(submittable.representationType);
        if (normalized) payload.representationType = normalized;
    }

    if ('categoryId' in submittable) {
        payload.categoryId = toTrimmedString(submittable.categoryId) ?? '';
    }

    if ('status' in submittable) {
        const normalized = toTrimmedString(submittable.status);
        if (normalized) payload.status = normalized.toUpperCase();
    }

    const normalizedActive = typeof active === 'boolean' ? active : undefined;
    if (payload.status === undefined && normalizedActive !== undefined) {
        payload.status = normalizedActive ? 'ACTIVE' : 'SUSPENDED';
    }

    if ('validFrom' in submittable) {
        payload.validFrom = toTrimmedString(submittable.validFrom) ?? '';
    }

    if ('validUntil' in submittable) {
        const normalized = toTrimmedString(submittable.validUntil);
        payload.validUntil = (normalized ?? '') as CreateRepresentationDto['validUntil'];
    }

    if ('grantedPowers' in submittable) {
        payload.grantedPowers = ensureStringArray(submittable.grantedPowers);
    }

    const stringFields: Array<keyof CreateRepresentationDto> = [
        'limitations',
        'legalDocumentId',
        'legalDocumentNumber',
        'registrationDate',
        'registrationOffice',
        'justification',
        'approvalDate',
        'approvedBy',
        'revokedDate',
        'revocationReason',
        'revokedBy',
        'notes',
        'lastUsedDate',
    ];

    stringFields.forEach((field) => {
        if (field in submittable) {
            const value = toTrimmedString((submittable as Record<string, unknown>)[field] as string | null | undefined);
            if (value !== undefined) {
                (payload as Record<string, unknown>)[field] = value as CreateRepresentationDto[typeof field];
            }
        }
    });

    if ('financialLimit' in submittable) {
        const numeric = toOptionalNumber(submittable.financialLimit);
        if (numeric !== undefined) payload.financialLimit = numeric;
    }

    if ('usageCount' in submittable) {
        const numeric = toOptionalNumber(submittable.usageCount);
        if (numeric !== undefined) payload.usageCount = numeric;
    }

    if ('allowsSubdelegation' in submittable) {
        payload.allowsSubdelegation = normalizeBoolean(submittable.allowsSubdelegation, false);
    }

    if ('requiresConfirmation' in submittable) {
        payload.requiresConfirmation = normalizeBoolean(submittable.requiresConfirmation, false);
    }

    if ('isMonitored' in submittable) {
        payload.isMonitored = normalizeBoolean(submittable.isMonitored, false);
    }

    if ('entitySupplierId' in submittable) {
        payload.entitySupplierId = normalizeEntityId(submittable.entitySupplierId) as CreateRepresentationDto['entitySupplierId'];
    }

    if ('entityPublicAgencyId' in submittable) {
        payload.entityPublicAgencyId = normalizeEntityId(submittable.entityPublicAgencyId) as CreateRepresentationDto['entityPublicAgencyId'];
    }

    return payload;
};

export const createRepresentationsFormService = () =>
    new BaseFormService<RepresentationRecord, RepresentationFormData, CreateRepresentationDto, Partial<CreateRepresentationDto>, RepresentationFilters>({
        basePath: '/representations',
        initialData: representationFormDefaults,
        normalizeRecord: normalizeRepresentationRecord,
        validationRules: {
            representativeUserId: [ValidationRules.required('Usuário representante é obrigatório')],
            representationType: [ValidationRules.required('Tipo de representação é obrigatório')],
            categoryId: [ValidationRules.required('Categoria é obrigatória')],
            status: [ValidationRules.required('Status é obrigatório')],
            validFrom: [ValidationRules.required('Data de início é obrigatória')],
            entitySupplierId: [entitySelectionRequired, entitySelectionExclusive],
            entityPublicAgencyId: [entitySelectionExclusive],
        },
        transformForCreate: (formData) => {
            const sanitized = sanitizeRepresentationPayload(formData);
            if (!sanitized.status) {
                sanitized.status = 'ACTIVE';
            }
            return sanitized as CreateRepresentationDto;
        },
        transformForUpdate: (formData: Partial<RepresentationFormData>) => sanitizeRepresentationPayload(formData),
        transformToForm: (record) => ({
            ...representationFormDefaults,
            representativeUserId: record.representativeUserId || '',
            requestId: record.requestId || '',
            representationType: record.representationType || '',
            categoryId: record.categoryId || '',
            status: record.status || 'ACTIVE',
            active: record.active ?? resolveRepresentationActive(record),
            validFrom: record.validFrom || representationFormDefaults.validFrom,
            validUntil: record.validUntil || '',
            grantedPowers: ensureStringArray(record.grantedPowers),
            limitations: record.limitations || '',
            financialLimit: record.financialLimit ?? undefined,
            legalDocumentId: record.legalDocumentId || '',
            legalDocumentNumber: record.legalDocumentNumber || '',
            registrationDate: record.registrationDate || '',
            registrationOffice: record.registrationOffice || '',
            justification: record.justification || '',
            approvalDate: record.approvalDate || '',
            approvedBy: record.approvedBy || '',
            revokedDate: record.revokedDate || '',
            revocationReason: record.revocationReason || '',
            revokedBy: record.revokedBy || '',
            usageCount: record.usageCount ?? undefined,
            lastUsedDate: record.lastUsedDate || '',
            notes: record.notes || '',
            allowsSubdelegation: record.allowsSubdelegation ?? false,
            requiresConfirmation: record.requiresConfirmation ?? false,
            isMonitored: record.isMonitored ?? false,
            entitySupplierId: record.entitySupplierId ?? null,
            entityPublicAgencyId: record.entityPublicAgencyId ?? null,
            createdAt: record.createdAt,
            updatedAt: record.updatedAt,
            createdBy: record.createdBy,
            createdByName: record.createdByName,
            createdByUser: record.createdByUser,
            updatedBy: record.updatedBy,
            updatedByName: record.updatedByName,
            updatedByUser: record.updatedByUser,
        }),
    });

export { representationFormDefaults };
